import { ApolloError } from '@apollo/client';
import {
  digitRule,
  lowercaseLetterRule,
  minLengthPasswordRule,
  specialCharRule,
  toCapitalizeCase,
  updatePasswordGql,
  uppercaseLetterRule,
  useTranslation,
} from '@gimlite/common';
import { SSOContextType } from '@gimlite/common/stores/sso.store';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useState } from 'react';
import { useDevice } from '../../hooks/useDevice.hook';
import { useStoreUIV1 } from '../../hooks/useStore.hook';
import { Position } from '../../types/component.type';
import { Avatar } from '../avatar/avatar.component';
import { Button } from '../button/button.component';
import { Col } from '../col/col.component';
import { Drawer, DrawerContext, DrawerType } from '../drawer/drawer.component';
import { Form } from '../form/form.component';
import { Icon, IconType } from '../icon/icon.component';
import { Input } from '../input/input.component';
import { PasswordStrengthValidator } from '../passwordStrengthValidator/passwordStrengthValidator.component';
import { Popover } from '../popover/popover.component';
import { Radio, RadioGroup } from '../radio/radio.component';
import { Row } from '../row/row.component';
import { Select } from '../select/select.component';
import { Space } from '../space/space.component';
import { Write } from '../write/write.component';
import './header.component.scss';

class Static {
  public static flagLangException: Record<string, string> = {
    EN: 'GB',
  };

  public static flagLang = (value: string): string => {
    const labeled = Static.langLabeled(value);
    const flagException = Static.flagLangException[labeled];
    return flagException ? flagException : labeled;
  };

  public static langLabeled = (lang: string): string => lang.split('_')[1];
}

export declare namespace HeaderGroupSectionType {
  type Props = {
    handleEvent: {
      close: () => void;
    };
    className?: string;
    data: {
      key: string;
      disabled?: boolean;
      items: Array<HeaderGroupSectionType.Data.Item | null>;
    };
  };

  namespace Data {
    type Item = {
      key: string;
      icon?: IconType.Config.Type;
      label: string;
      extra?:
        | { type: 'value'; text: string }
        | { type: 'drawer'; node: DrawerType.Data }
        | { type: 'event'; event: () => void }
        | ({ type: 'depth' } & HeaderGroupSectionType.Props['data']);
      disabled?: boolean;
    };
  }
}

const HeaderGroupSection = ({
  data: { items, disabled },
  handleEvent: { close },
}: HeaderGroupSectionType.Props) => {
  const { isDesktop } = useDevice();

  return items.filter((v) => v !== null).length > 0 ? (
    <div className="accountManager__account__group">
      {items
        .filter((v) => v !== null)
        .filter(({ disabled = false }) => !disabled)
        .map(({ key, icon, label, extra }) => {
          return (
            <div key={key} className="accountManager__account__group__option">
              <div className="accountManager__account__group__option__subject">
                {icon && (
                  <Icon
                    className="accountManager__account__group__option__subject__icon"
                    config={{
                      type: icon,
                      color: 'label',
                      size: 'xsmall',
                    }}
                  ></Icon>
                )}
                <div className="accountManager__account__group__option__subject__label">
                  <Write
                    data={{ item: label }}
                    config={{
                      mode: 'label-value',
                      color: 'yang',
                      wrap: false,
                    }}
                  />
                </div>
              </div>
              {extra && (
                <div className="accountManager__account__group__option__extra">
                  {extra.type === 'drawer' ? (
                    <Drawer
                      data={extra.node}
                      config={{
                        size: isDesktop ? 'xsmall' : 'large',
                        placement: isDesktop ? 'right' : 'bottom',
                      }}
                    >
                      {
                        <Icon
                          config={{
                            color: 'primary',
                            type: 'faChevronRightSolid',
                            size: 'xxsmall',
                          }}
                        />
                      }
                    </Drawer>
                  ) : extra.type === 'event' ? (
                    <div
                      onClick={() => {
                        extra.event();
                        close();
                      }}
                      className="event-trigger"
                    ></div>
                  ) : extra.type === 'value' ? (
                    <Write
                      data={{ item: extra.text }}
                      config={{ mode: 'label' }}
                    ></Write>
                  ) : (
                    <Drawer
                      data={
                        <HeaderGroupSection
                          handleEvent={{
                            close: () => {
                              close();
                            },
                          }}
                          data={{
                            items: extra.items,
                            key: extra.key,
                            disabled: extra.disabled,
                          }}
                        />
                      }
                      config={{
                        size: isDesktop ? 'xsmall' : 'large',
                        placement: isDesktop ? 'right' : 'bottom',
                      }}
                    >
                      {
                        <Icon
                          config={{
                            color: 'primary',
                            type: 'faChevronRightSolid',
                            size: 'xxsmall',
                          }}
                        />
                      }
                    </Drawer>
                  )}
                </div>
              )}
            </div>
          );
        })}
    </div>
  ) : null;
};

const ThemeDrawerForm = observer(() => {
  const { ThemeStore } = useStoreUIV1();
  const { t } = useTranslation();

  return (
    <Form
      data={{
        value: {
          theme: ThemeStore.theme,
        },
      }}
      handleEvent={{
        change: (value: any) => {
          if (value?.theme) {
            ThemeStore.setTheme(value.theme);
          }
        },
      }}
      config={{
        element: { width: 'full', way: 'vertical' },
      }}
    >
      <Form.Item config={{ name: 'theme' }}>
        <RadioGroup>
          <Radio
            data={{
              value: 'light',
              label: t('watermelon-light'),
            }}
          ></Radio>
          <Radio
            data={{
              value: 'dark',
              label: t('watermelon-dark'),
            }}
          ></Radio>
        </RadioGroup>
      </Form.Item>
    </Form>
  );
});

declare namespace HeaderGroupType {
  type Props = {
    isConnected: boolean;
    features: {
      shortcut?: HeaderType.Config.Shortcut[];
      tfa?: { state: boolean; email: string };
      updatePassword?: boolean;
      lang?: boolean;
      theme?: boolean;
      versions?: {
        environment?: string | null;
        ui?: string | null;
        front?: string | null;
        common?: string | null;
      };
      logout: () => void;
    };
  };
}

const HeaderGroup = observer(
  ({
    isConnected,
    features: {
      shortcut,
      tfa,
      updatePassword,
      lang = true,
      theme = true,
      versions,
      logout,
    },
  }: HeaderGroupType.Props) => {
    const {
      I18NStore,
      NotificationStore,
      WizardStore,
      GQLStore,
      DialogStore,
      SSOStore,
    } = useStoreUIV1();
    const { t } = useTranslation();
    const [passwordValidFormat, setPasswordValidForm] = useState(false);
    const [passwordValidSame, setPasswordValidSame] = useState(false);
    const [passwordValues, setPasswordValues] = useState<Object>({});
    const [passwordStrengthValid, setPasswordStrengthValid] = useState(false);
    const [passwordTouched, setPasswordTouched] = useState(false);

    const langAvailable = I18NStore.langsAvailable;
    const langCurrent = I18NStore.lang;

    return (
      lang && (
        <DrawerContext.Consumer>
          {(drawer) => {
            return [
              {
                key: 'group1',
                items: [
                  shortcut && isConnected
                    ? {
                        key: 'shortcut',
                        label: t('watermelon-shortcut'),
                        icon: 'faArrowsUpDownLeftRightSolid',
                        extra: {
                          type: 'depth',
                          key: 'shortcut',
                          items: shortcut
                            ? shortcut.map(({ event, label, icon }, index) => ({
                                key: `form-${index}`,
                                label,
                                icon,
                                extra: {
                                  type: 'event',
                                  event: () => {
                                    event();
                                  },
                                },
                              }))
                            : [],
                        },
                      }
                    : null,
                ] as Array<HeaderGroupSectionType.Data.Item | null>,
              },
              {
                key: 'group2',
                items: [
                  tfa && isConnected && tfa.state
                    ? {
                        key: '2fa',
                        label: t('watermelon-update-2FA'),
                        icon: 'partnerGoogleAuthentificator',
                        extra: {
                          type: 'event',
                          event: () => {
                            DialogStore.select({
                              title: t('watermelon-otp.update.title'),

                              description: t(
                                'watermelon-otp.update.description',
                              ),
                              confirm: async () => {
                                const key = crypto.randomUUID();

                                NotificationStore.emit({
                                  mode: 'LOADING',
                                  content: t('watermelon-otpUpdateProgress'),
                                  key,
                                });

                                const activeOtp = await SSOStore.activeOtp({
                                  client: GQLStore.client!,
                                  introspection: GQLStore.introspection!,
                                });

                                if (activeOtp) {
                                  NotificationStore.emit({
                                    mode: 'SUCCESS',
                                    content: t('watermelon-otpIsUpdated'),
                                    key,
                                  });

                                  SSOStore.otpIsActive = true;

                                  location.reload();
                                } else {
                                  NotificationStore.emit({
                                    mode: 'ERROR',
                                    content: t('watermelon-otpIsNotUpdated'),
                                    key,
                                  });
                                }
                              },
                            });
                          },
                        },
                      }
                    : null,
                  updatePassword && isConnected
                    ? {
                        key: 'password',
                        label: t('watermelon-update-password'),
                        icon: 'faLockSolid',
                        extra: {
                          type: 'drawer',
                          node: (
                            <Form
                              data={{
                                value: passwordValues,
                              }}
                              handleEvent={{
                                submit: async ({
                                  currentPassword,
                                  newPassword,
                                }: any) => {
                                  try {
                                    const mutate =
                                      await GQLStore?.client?.mutate<
                                        {
                                          updatePassword: { success: boolean };
                                        },
                                        {
                                          input: {
                                            currentPassword: string;
                                            newPassword: string;
                                          };
                                        }
                                      >({
                                        mutation: updatePasswordGql,
                                        variables: {
                                          input: {
                                            currentPassword,
                                            newPassword,
                                          },
                                        },
                                      });

                                    if (mutate?.data?.updatePassword?.success) {
                                      NotificationStore.emit({
                                        mode: 'SUCCESS',
                                        content: t(
                                          'watermelon-thePasswordHasBeenChanged',
                                        ),
                                      });
                                      setPasswordValues(() => {});
                                      setPasswordTouched(false);
                                      setPasswordStrengthValid(false);
                                    } else {
                                      NotificationStore.emit({
                                        mode: 'ERROR',
                                        content: t(
                                          'watermelon-passwordCouldNotBeChanged',
                                        ),
                                      });
                                    }
                                  } catch (error: unknown) {
                                    if (
                                      error instanceof ApolloError &&
                                      error.message === 'MACC_1'
                                    ) {
                                      NotificationStore.emit({
                                        mode: 'ERROR',
                                        content: t(
                                          'watermelon-theCurrentPasswordIsIncorrect',
                                        ),
                                      });
                                    } else {
                                      NotificationStore.emit({
                                        mode: 'ERROR',
                                        content: t('watermelon-SERVER_PROBLEM'),
                                      });
                                    }
                                  }
                                },
                                validate: (isValid) => {
                                  setPasswordValidForm(() => isValid);
                                },
                                change: (values) => {
                                  setPasswordValues(() => values);
                                  setPasswordValidSame(
                                    () =>
                                      values.newPassword ===
                                      values.confirmNewPassword,
                                  );
                                },
                              }}
                              config={{
                                validateOnChange: true,
                                element: { width: 'full', way: 'vertical' },
                              }}
                            >
                              <Form.Item
                                config={{
                                  name: 'currentPassword',
                                  label: t('watermelon-currentPassword'),
                                  rules: [{ required: true }],
                                }}
                              >
                                <Input
                                  config={{ type: { name: 'password' } }}
                                ></Input>
                              </Form.Item>
                              <Space config={{ way: 'vertical', count: 2 }} />
                              <Form.Item
                                config={{
                                  name: 'newPassword',
                                  label: t('watermelon-newPassword'),
                                  rules: [
                                    { required: true },
                                    {
                                      validator: minLengthPasswordRule,
                                    },
                                    {
                                      validator: uppercaseLetterRule,
                                    },
                                    {
                                      validator: lowercaseLetterRule,
                                    },
                                    {
                                      validator: digitRule,
                                    },
                                    {
                                      validator: specialCharRule,
                                    },
                                  ],
                                }}
                              >
                                <Input
                                  config={{ type: { name: 'password' } }}
                                  handleEvent={{
                                    input: () => setPasswordTouched(true),
                                  }}
                                ></Input>
                              </Form.Item>

                              {/* Validation de la force du mot de passe */}
                              <PasswordStrengthValidator
                                data={{
                                  password:
                                    (passwordValues as any)?.newPassword || '',
                                  touched: passwordTouched,
                                  minLength: 12,
                                }}
                                config={{
                                  showStrengthBar: true,
                                  showCriteria: true,
                                }}
                                handleEvent={{
                                  change: (result) => {
                                    setPasswordStrengthValid(result.isValid);
                                  },
                                }}
                                msgFn={(key: string) =>
                                  t(`watermelon-${key}` as any) || key
                                }
                              />

                              <Space config={{ way: 'vertical', count: 2 }} />
                              <Form.Item
                                config={{
                                  name: 'confirmNewPassword',
                                  label: t('watermelon-confirmNewPassword'),
                                  rules: [
                                    { required: true },
                                    {
                                      validator: minLengthPasswordRule,
                                    },
                                    {
                                      validator: uppercaseLetterRule,
                                    },
                                    {
                                      validator: lowercaseLetterRule,
                                    },
                                    {
                                      validator: digitRule,
                                    },
                                    {
                                      validator: specialCharRule,
                                    },
                                  ],
                                }}
                              >
                                <Input
                                  config={{ type: { name: 'password' } }}
                                  handleEvent={{
                                    input: () => setPasswordTouched(true),
                                  }}
                                ></Input>
                              </Form.Item>

                              {/* Message d'erreur si les mots de passe ne correspondent pas */}
                              {passwordTouched &&
                                (passwordValues as any)?.newPassword &&
                                (passwordValues as any)?.confirmNewPassword &&
                                !passwordValidSame && (
                                  <div style={{ marginTop: '4px' }}>
                                    <Col
                                      config={{
                                        width: 'full',
                                        vertical: 'center',
                                        horizontal: 'start',
                                      }}
                                    >
                                      <Write
                                        data={{
                                          item: t(
                                            'watermelon-passwordsAreDifferent',
                                          ),
                                        }}
                                        config={{
                                          color: 'error',
                                          mode: 'key-small-light',
                                        }}
                                      />
                                    </Col>
                                  </div>
                                )}

                              <Space config={{ way: 'vertical', count: 2 }} />
                              <Button
                                config={{
                                  size: 'small',
                                  text: t('watermelon-submit'),
                                  type: {
                                    value: 'submit',
                                  },
                                  disabled: !(
                                    passwordValidFormat &&
                                    passwordValidSame &&
                                    passwordStrengthValid
                                  ),
                                }}
                              ></Button>
                            </Form>
                          ),
                        },
                      }
                    : null,
                  lang
                    ? {
                        key: 'lang',
                        label: t('watermelon-update-lang'),
                        icon: 'faEarthEuropeSolid',
                        extra: {
                          type: 'drawer',
                          node: (
                            <Form
                              data={{
                                value: {
                                  lang: langCurrent,
                                },
                              }}
                              handleEvent={{
                                change: async (value) => {
                                  I18NStore.setLang(value.lang);

                                  await I18NStore.updateLang({
                                    client: GQLStore.client!,
                                    introspection: GQLStore.introspection!,
                                    lang: value.lang,
                                  });
                                },
                              }}
                              config={{
                                element: { width: 'full', way: 'vertical' },
                              }}
                            >
                              <Form.Item
                                data={{ defaultValue: langCurrent }}
                                config={{ name: 'lang' }}
                              >
                                <Select
                                  data={{
                                    items: langAvailable
                                      ? langAvailable.map(
                                          (
                                            lang: string,
                                          ): {
                                            label: React.ReactNode;
                                            value: string;
                                          } => ({
                                            label: (
                                              <div className="select-lang-item">
                                                <img
                                                  src={`https://flagsapi.com/${Static.flagLang(
                                                    lang,
                                                  )}/shiny/32.png`}
                                                ></img>
                                                <span>
                                                  {Static.langLabeled(lang)}
                                                </span>
                                              </div>
                                            ),
                                            value: lang,
                                          }),
                                        )
                                      : [],
                                  }}
                                ></Select>
                              </Form.Item>
                            </Form>
                          ),
                        },
                      }
                    : null,
                  theme
                    ? {
                        key: 'theme',
                        label: t('watermelon-update-theme'),
                        icon: 'faPaintRollerSolid',
                        extra: {
                          type: 'drawer',
                          node: <ThemeDrawerForm />,
                        },
                      }
                    : null,
                ] as Array<HeaderGroupSectionType.Data.Item | null>,
              },
              {
                key: 'groupVersion',
                disabled: versions?.environment === 'production',
                items: [
                  {
                    key: 'environnement',
                    label: t('watermelon-environment'),
                    icon: 'faSeedlingSolid',
                    extra: {
                      type: 'value',
                      text:
                        versions?.environment || t('watermelon-not-defined'),
                    },
                  },
                  {
                    key: 'ui',
                    label: 'UI',
                    icon: 'faCodeForkSolid',
                    extra: {
                      type: 'value',
                      text: versions?.ui || t('watermelon-not-defined'),
                    },
                  },
                  {
                    key: 'common',
                    label: 'Common',
                    icon: 'faCodeForkSolid',
                    extra: {
                      type: 'value',
                      text: versions?.common || t('watermelon-not-defined'),
                    },
                  },
                  {
                    key: 'front',
                    label: t('watermelon-application'),
                    icon: 'faCodeMergeSolid',
                    extra: {
                      type: 'value',
                      text: versions?.front || t('watermelon-not-defined'),
                    },
                  },
                ],
              },
              {
                key: 'link',
                disabled: !isConnected,
                items: [
                  {
                    key: 'logout',
                    label: t('watermelon-logout'),
                    icon: 'faArrowRightFromBracketSolid',
                    extra: {
                      type: 'event',
                      event: () => {
                        logout();
                      },
                    },
                  },
                ],
              },
            ]
              .filter(({ disabled = false }) => !disabled)
              .map(({ items, key, disabled }, index) => {
                return (
                  <HeaderGroupSection
                    handleEvent={{
                      close: () => {
                        drawer?.close();
                      },
                    }}
                    key={index}
                    data={{ items: items as any, key, disabled }}
                  />
                );
              });
          }}
        </DrawerContext.Consumer>
      )
    );
  },
);

export declare namespace HeaderType {
  type Props = {
    className?: string;
    config?: {
      position?: Extract<Position, 'absolute' | 'fixed' | 'static' | 'sticky'>;
      logo?: HeaderType.Config.Logo;
      shortcut?: HeaderType.Config.Shortcut[];
      extra?: HeaderType.Config.Extra;
      withAccount?: boolean;
    };
  };

  namespace Config {
    type Extra = React.ReactNode;
    type Logo = React.ReactNode;
    type Shortcut = {
      icon?: HeaderGroupSectionType.Data.Item['icon'];
      label: HeaderGroupSectionType.Data.Item['label'];
      event: () => void;
    };
  }
}

export const Header = observer(
  ({
    className = '',
    config: {
      position = 'static',
      logo,
      shortcut,
      extra,
      withAccount = true,
    } = {},
  }: HeaderType.Props) => {
    const { isDesktop } = useDevice();
    const { t } = useTranslation();
    const { SSOStore, AppStore } = useStoreUIV1();

    const mode = useMemo<'UNCONNECTED' | 'SSO_PARENT' | 'SSO_CHILDREN'>(() => {
      if (!SSOStore.authenticated) {
        return 'UNCONNECTED';
      } else if (SSOStore?.mode?.type === 'parent') {
        return 'SSO_PARENT';
      } else {
        return 'SSO_CHILDREN';
      }
    }, [SSOStore, window.location]);

    return (
      <header
        id="header"
        className={`
          header 
          header--position--${position}
          ${className}
        `}
      >
        <div className="header__logo">{logo}</div>
        <div className="header__extra">{extra}</div>
        {isDesktop &&
          withAccount &&
          (mode === 'UNCONNECTED' ? (
            <AccountManager
              group={{
                isConnected: false,
                features: {
                  lang: true,
                  theme: true,
                  logout: () => {},
                  updatePassword: false,

                  versions: {
                    environment: AppStore?.version?.env,
                    front: AppStore?.version?.front,
                    ui: AppStore?.version?.ui,
                    common: AppStore?.version?.common,
                  },
                },
              }}
            >
              <Row
                config={{ vertical: 'center', horizontal: 'center', gap: 8 }}
              >
                <Icon
                  config={{
                    type: 'faGearSolid',
                    color: 'primary-over',
                    size: 'large',
                  }}
                />
              </Row>
            </AccountManager>
          ) : mode === 'SSO_PARENT' ? (
            <AccountManager
              account={{
                firstName: SSOStore?.account?.firstName,
                lastName: SSOStore?.account?.lastName,
                email: SSOStore?.account?.email,
              }}
              group={{
                isConnected: !!SSOStore.authenticated,
                features: {
                  lang: true,
                  shortcut,
                  theme: true,
                  tfa: {
                    state: SSOStore.otpIsActive,
                    email: SSOStore?.account?.email!,
                  },
                  updatePassword: true,
                  logout: () => {
                    SSOStore.logout();
                  },
                  versions: {
                    environment: AppStore?.version?.env,
                    front: AppStore?.version?.front,
                    ui: AppStore?.version?.ui,
                    common: AppStore?.version?.common,
                  },
                },
              }}
            >
              <div className="header__params">
                <div className="header__params__item">
                  <AccountAvatar
                    colorInverse={true}
                    account={{
                      firstName: SSOStore?.account?.firstName,
                      lastName: SSOStore?.account?.lastName,
                    }}
                  />
                  <AccountNamise
                    colorInverse={true}
                    account={{
                      firstName: SSOStore?.account?.firstName,
                      lastName: SSOStore?.account?.lastName,
                    }}
                  ></AccountNamise>
                </div>
              </div>
            </AccountManager>
          ) : (
            <div className="header__params">
              <AccountManager
                account={{
                  firstName: SSOStore?.account?.firstName,
                  lastName: SSOStore?.account?.lastName,
                  email: SSOStore?.account?.email,
                }}
                group={{
                  isConnected: !!SSOStore.authenticated,
                  features: {
                    lang: true,
                    shortcut,
                    theme: true,
                    tfa: {
                      state: SSOStore.otpIsActive,
                      email: SSOStore?.account?.email!,
                    },
                    updatePassword: true,
                    logout: () => {
                      SSOStore.logout();
                    },
                    versions: {
                      environment: AppStore?.version?.env,
                      front: AppStore?.version?.front,
                      ui: AppStore?.version?.ui,
                      common: AppStore?.version?.common,
                    },
                  },
                }}
              >
                <div className="header__params__item">
                  <AccountAvatar
                    colorInverse={true}
                    account={{
                      firstName: SSOStore?.account?.firstName,
                      lastName: SSOStore?.account?.lastName,
                    }}
                  />
                  <AccountNamise
                    colorInverse={true}
                    account={{
                      firstName: SSOStore?.account?.firstName,
                      lastName: SSOStore?.account?.lastName,
                    }}
                  ></AccountNamise>
                </div>
              </AccountManager>
              {SSOStore?.mode?.type !== 'independent' && (
                <AccountSwitchApp
                  roles={SSOStore?.roles}
                  resources={SSOStore?.resources!}
                >
                  <Icon
                    config={{
                      type: 'customGridMenu',
                      color: 'primary-over',
                      size: 'large',
                    }}
                  />
                </AccountSwitchApp>
              )}
            </div>
          ))}
      </header>
    );
  },
);

const AccountManager = ({
  children,
  group,
  account,
}: {
  group: HeaderGroupType.Props;
  account?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const { isDesktop } = useDevice();

  return (
    <div className="accountManager">
      <Drawer
        data={
          <div className="accountManager__account">
            {account && Object.values(account).filter(Boolean).length > 0 && (
              <div className="accountManager__account__identity">
                <AccountAvatar colorInverse={false} account={account} />
                <div className="accountManager__account__identity__names">
                  <AccountNamise colorInverse={false} account={account} />
                  <Write
                    data={{
                      item: account?.email || t('watermelon-not-defined-email'),
                    }}
                    config={{ mode: 'namise-description' }}
                  />
                </div>
              </div>
            )}
            <HeaderGroup
              features={group.features}
              isConnected={group.isConnected}
            ></HeaderGroup>
          </div>
        }
        config={{
          size: isDesktop ? 'xsmall' : 'large',
          placement: isDesktop ? 'right' : 'bottom',
        }}
      >
        {children}
      </Drawer>
    </div>
  );
};

const AccountSwitchApp = observer(
  ({
    resources,
    children,
  }: {
    children: React.ReactNode;
    roles?: Array<string>;
    resources: SSOContextType['resources'];
  }) => {
    return (
      <Popover
        config={{ padding: false, placement: 'topRight' }}
        data={
          <div className="accountSwitchApp">
            <div className="accountSwitchApp__body">
              {resources?.modules
                .filter(({ url }) => !url?.startsWith(window.location.origin))
                .map(
                  ({ name, label, url, canUse, color, description, title }) => (
                    <div
                      onClick={() => {
                        if (canUse) window.location.replace(url);
                      }}
                      style={{ backgroundColor: color }}
                      key={name}
                      className={`
                      accountSwitchApp__body__app 
                      accountSwitchApp__body__app--${
                        canUse ? 'active' : 'inactive'
                      }
                      
                      `}
                    >
                      <Write
                        data={{ item: label }}
                        config={{
                          mode: 'namise',
                          wrap: true,
                          align: 'center',
                          color: 'black',
                        }}
                      ></Write>
                    </div>
                  ),
                )}
            </div>
            <div
              onClick={() => {
                if (resources?.parentUrl) {
                  window.location.replace(resources.parentUrl);
                }
              }}
              className="accountSwitchApp__footer"
            >
              <Write
                data={{ item: 'Retour à l’accueil' }}
                config={{ mode: 'namise', color: 'primary-over' }}
              ></Write>
              <Icon
                config={{
                  type: 'faArrowRightSolid',
                  color: 'primary-over',
                  size: 'medium',
                }}
              />
            </div>
          </div>
        }
      >
        {children}
      </Popover>
    );
  },
);

const AccountNamise = ({
  account,
  colorInverse,
}: {
  account: { firstName?: string | null; lastName?: string | null };
  colorInverse: boolean;
}) => {
  const { lang, t } = useTranslation();

  const fullName = useMemo(() => {
    return account?.firstName && account?.lastName
      ? `${toCapitalizeCase(account?.firstName)} ${toCapitalizeCase(
          account?.lastName,
        )}`
      : account?.firstName && !account?.lastName
        ? `${toCapitalizeCase(account?.firstName)}`
        : !account?.firstName && account?.lastName
          ? `${toCapitalizeCase(account?.lastName)} `
          : t('watermelon-not-defined-names');
  }, [account, lang]);

  return (
    <Write
      data={{
        item: fullName,
      }}
      config={{
        color: colorInverse ? 'primary-over' : 'primary',
        mode: 'link',
      }}
    />
  );
};

const AccountAvatar = ({
  account,
  colorInverse,
}: {
  account: { firstName?: string | null; lastName?: string | null };
  colorInverse: boolean;
}) => {
  return (
    <Avatar
      data={{
        firstName: account?.firstName || '',
        lastName: account?.lastName || '',
      }}
      config={{
        backgroundColor: colorInverse ? 'primary-over' : 'primary',
        color: colorInverse ? 'primary' : 'primary-over',
      }}
    ></Avatar>
  );
};
